import React from 'react';
import {
  Play,
  Pause,
  RotateCcw,
  Mic,
  Maximize,
  Minimize,
  Sliders,
  ArrowLeft,
  Plus,
  Minus,
} from 'lucide-react';
import { PlayState, ControlSource } from '../core/scroll/types';

interface ControlBarProps {
  playState: PlayState;
  controlSource: ControlSource;
  isVoiceActive: boolean;
  isVoiceHold: boolean;
  speed: number;
  fontSize: number;
  isFullscreen: boolean;
  progressFraction: number;
  visible: boolean;
  onPlayPause: () => void;
  onReset: () => void;
  onSpeedDelta: (deltaPercent: number) => void;
  onFontDelta: (deltaPx: number) => void;
  onToggleVoice: () => void;
  onToggleFullscreen: () => void;
  onOpenSettings: () => void;
  onBackToEditor: () => void;
}

export const ControlBar: React.FC<ControlBarProps> = ({
  playState,
  controlSource,
  isVoiceActive,
  isVoiceHold,
  speed,
  fontSize,
  isFullscreen,
  progressFraction,
  visible,
  onPlayPause,
  onReset,
  onSpeedDelta,
  onFontDelta,
  onToggleVoice,
  onToggleFullscreen,
  onOpenSettings,
  onBackToEditor,
}) => {
  const isPlaying = playState === 'playing';

  // Blur active button on click to prevent Space bar from re-triggering it
  const handleButtonClick = (action: () => void) => (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.blur();
    action();
  };

  return (
    <div
      className={`fixed bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 z-50 transition-all duration-300 pointer-events-auto max-w-[96vw] ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8 pointer-events-none'
      }`}
      role="toolbar"
      aria-label="Prompter controls"
    >
      <div className="relative flex items-center space-x-1 sm:space-x-2 bg-neutral-900/90 backdrop-blur-md border border-neutral-700/80 rounded-full px-2 sm:px-4 py-1.5 sm:py-2 shadow-2xl text-white select-none overflow-x-auto no-scrollbar">
        {/* Progress bar along the top edge */}
        <div
          className="absolute top-0 left-4 right-4 h-0.5 bg-neutral-800 rounded-full overflow-hidden"
          aria-hidden="true"
        >
          <div
            className="h-full bg-blue-500 transition-all duration-100"
            style={{ width: `${Math.min(100, Math.max(0, progressFraction * 100))}%` }}
          />
        </div>

        {/* Back to Editor */}
        <button
          onClick={handleButtonClick(onBackToEditor)}
          className="p-2 hover:bg-neutral-800 rounded-full text-neutral-300 hover:text-white transition focus:ring-2 focus:ring-blue-500 focus:outline-hidden"
          title="Back to Editor"
          aria-label="Back to Editor"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>

        <div className="h-5 w-px bg-neutral-700 mx-1" aria-hidden="true" />

        {/* Play / Pause / Hold */}
        <button
          onClick={handleButtonClick(onPlayPause)}
          className={`p-2.5 rounded-full text-white transition focus:ring-2 focus:ring-blue-500 focus:outline-hidden ${
            isPlaying
              ? isVoiceActive && isVoiceHold
                ? 'bg-amber-600 hover:bg-amber-500'
                : 'bg-neutral-700 hover:bg-neutral-600'
              : 'bg-blue-600 hover:bg-blue-500'
          }`}
          title={
            controlSource === 'voice'
              ? isVoiceHold
                ? 'Resume Voice Follow (Space)'
                : 'Hold Voice Follow (Space)'
              : isPlaying
              ? 'Pause (Space)'
              : 'Start (Space)'
          }
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? (
            isVoiceActive && isVoiceHold ? (
              <Play className="w-5 h-5 fill-current" />
            ) : (
              <Pause className="w-5 h-5 fill-current" />
            )
          ) : (
            <Play className="w-5 h-5 fill-current ml-0.5" />
          )}
        </button>

        {/* Reset / Stop */}
        <button
          onClick={handleButtonClick(onReset)}
          className="p-2 hover:bg-neutral-800 rounded-full text-neutral-300 hover:text-white transition focus:ring-2 focus:ring-blue-500 focus:outline-hidden"
          title="Reset to start (Home)"
          aria-label="Reset to start"
        >
          <RotateCcw className="w-5 h-5" />
        </button>

        <div className="hidden sm:block h-5 w-px bg-neutral-700 mx-1" aria-hidden="true" />

        {/* Speed Controls (- / +) */}
        <div className="flex items-center space-x-0.5 sm:space-x-1 px-0.5 sm:px-1">
          <button
            onClick={handleButtonClick(() => onSpeedDelta(-4))}
            className="p-1 sm:p-1.5 hover:bg-neutral-800 rounded-full text-neutral-300 hover:text-white transition"
            title="Slower (↓)"
            aria-label="Decrease speed"
          >
            <Minus className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          </button>
          <span className="text-[11px] sm:text-xs font-mono font-medium px-0.5 sm:px-1 min-w-[2.8rem] sm:min-w-[3.5rem] text-center text-neutral-200">
            {Math.round(speed)} <span className="hidden sm:inline">px/s</span>
          </span>
          <button
            onClick={handleButtonClick(() => onSpeedDelta(4))}
            className="p-1 sm:p-1.5 hover:bg-neutral-800 rounded-full text-neutral-300 hover:text-white transition"
            title="Faster (↑)"
            aria-label="Increase speed"
          >
            <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          </button>
        </div>

        <div className="hidden sm:block h-5 w-px bg-neutral-700 mx-1" aria-hidden="true" />

        {/* Font Size Controls (- / +) */}
        <div className="flex items-center space-x-0.5 sm:space-x-1 px-0.5 sm:px-1">
          <button
            onClick={handleButtonClick(() => onFontDelta(-4))}
            className="p-1 sm:p-1.5 hover:bg-neutral-800 rounded-full text-neutral-300 hover:text-white transition"
            title="Smaller font (Ctrl -)"
            aria-label="Decrease font size"
          >
            <Minus className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          </button>
          <span className="text-[11px] sm:text-xs font-mono font-medium px-0.5 sm:px-1 min-w-[2.2rem] sm:min-w-[3rem] text-center text-neutral-200">
            {Math.round(fontSize)} <span className="hidden sm:inline">px</span>
          </span>
          <button
            onClick={handleButtonClick(() => onFontDelta(4))}
            className="p-1 sm:p-1.5 hover:bg-neutral-800 rounded-full text-neutral-300 hover:text-white transition"
            title="Larger font (Ctrl +)"
            aria-label="Increase font size"
          >
            <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          </button>
        </div>

        <div className="hidden sm:block h-5 w-px bg-neutral-700 mx-1" aria-hidden="true" />

        {/* Voice Follow Mode Toggle */}
        <button
          onClick={handleButtonClick(onToggleVoice)}
          className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition focus:ring-2 focus:ring-blue-500 focus:outline-hidden ${
            controlSource === 'voice'
              ? 'bg-emerald-600 text-white'
              : 'hover:bg-neutral-800 text-neutral-300 hover:text-white'
          }`}
          title="Toggle Voice Follow mode (V)"
          aria-label="Toggle Voice Follow"
        >
          <Mic className="w-4 h-4" />
          <span className="hidden sm:inline">Voice</span>
          {controlSource === 'voice' && (
            <span
              className={`w-2 h-2 rounded-full ${
                isVoiceHold ? 'bg-amber-400' : isVoiceActive ? 'bg-emerald-300 animate-pulse' : 'bg-red-400'
              }`}
              aria-hidden="true"
            />
          )}
        </button>

        {/* Fullscreen Toggle */}
        <button
          onClick={handleButtonClick(onToggleFullscreen)}
          className="p-2 hover:bg-neutral-800 rounded-full text-neutral-300 hover:text-white transition focus:ring-2 focus:ring-blue-500 focus:outline-hidden"
          title="Toggle Fullscreen (F)"
          aria-label="Toggle Fullscreen"
        >
          {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
        </button>

        {/* Quick Settings */}
        <button
          onClick={handleButtonClick(onOpenSettings)}
          className="p-2 hover:bg-neutral-800 rounded-full text-neutral-300 hover:text-white transition focus:ring-2 focus:ring-blue-500 focus:outline-hidden"
          title="Settings"
          aria-label="Open settings"
        >
          <Sliders className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};
